import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LogIn, Search, Monitor } from "lucide-react";

export const fmtDateTime = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

export const browserLabel = (ua = "") => {
  if (!ua) return "Sconosciuto";
  const u = ua.toLowerCase();
  let b = "Browser";
  if (u.includes("edg")) b = "Edge"; else if (u.includes("chrome") && !u.includes("chromium")) b = "Chrome";
  else if (u.includes("firefox")) b = "Firefox"; else if (u.includes("safari")) b = "Safari";
  else if (u.includes("curl")) b = "curl"; else if (u.includes("okhttp")) b = "App";
  let os = ""; if (u.includes("android")) os = "Android"; else if (u.includes("iphone") || u.includes("ipad")) os = "iOS";
  else if (u.includes("windows")) os = "Windows"; else if (u.includes("mac os")) os = "macOS"; else if (u.includes("linux")) os = "Linux";
  return os ? `${b} · ${os}` : b;
};

const METHOD = { password: { label: "Password", cls: "bg-slate-500 hover:bg-slate-500" }, google: { label: "Google", cls: "bg-blue-600 hover:bg-blue-600" }, onetap: { label: "Google 1-Tap", cls: "bg-blue-500 hover:bg-blue-500" }, "2fa": { label: "2FA", cls: "bg-teal-600 hover:bg-teal-600" }, impersonate: { label: "Impersonato (admin)", cls: "bg-fuchsia-600 hover:bg-fuchsia-600" }, "stop-impersonate": { label: "Fine impersonazione", cls: "bg-fuchsia-800 hover:bg-fuchsia-800" } };
const ROLE_LABEL = { admin: "Admin", dealer: "Dealer", customer: "Cliente", agent: "Agente", support: "Support" };

export default function AdminAccessLogs() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [method, setMethod] = useState("all");
  const [days, setDays] = useState("90");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { days, limit: 500 };
    if (q.trim()) params.email = q.trim();
    if (role !== "all") params.role = role;
    if (method !== "all") params.method = method;
    api.get("/admin/access-logs", { params }).then((r) => setLogs(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [q, role, method, days]);
  useEffect(() => { load(); }, [role, method, days]); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="admin-access-logs-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Sicurezza</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><LogIn className="w-7 h-7 text-primary" /> Log accessi</h1>
        <p className="text-sm text-muted-foreground mt-1">Tutti gli accessi effettuati: data/ora, dispositivo (browser) e metodo. Conservazione 90 giorni.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Cerca per email…" className="pl-9" data-testid="access-search-input" />
        </div>
        <Button variant="outline" onClick={load} data-testid="access-search-button">Cerca</Button>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-9 w-40" data-testid="access-role-filter"><SelectValue placeholder="Ruolo" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i ruoli</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="dealer">Dealer</SelectItem><SelectItem value="customer">Cliente</SelectItem><SelectItem value="agent">Agente</SelectItem><SelectItem value="support">Support</SelectItem></SelectContent>
        </Select>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="h-9 w-40" data-testid="access-method-filter"><SelectValue placeholder="Metodo" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i metodi</SelectItem><SelectItem value="password">Password</SelectItem><SelectItem value="google">Google</SelectItem><SelectItem value="onetap">Google 1-Tap</SelectItem><SelectItem value="2fa">2FA</SelectItem></SelectContent>
        </Select>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="h-9 w-36" data-testid="access-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="1">Oggi</SelectItem><SelectItem value="7">7 giorni</SelectItem><SelectItem value="30">30 giorni</SelectItem><SelectItem value="90">90 giorni</SelectItem></SelectContent>
        </Select>
      </div>

      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow><TableHead>Data e ora</TableHead><TableHead>Utente</TableHead><TableHead>Ruolo</TableHead><TableHead>Metodo</TableHead><TableHead>Dispositivo</TableHead><TableHead>IP</TableHead></TableRow></TableHeader>
          <TableBody>
            {logs.map((l) => (
              <TableRow key={l.id} data-testid={`access-row-${l.id}`}>
                <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(l.at)}</TableCell>
                <TableCell className="text-sm"><span className="font-medium">{l.name || "—"}</span><br /><span className="text-muted-foreground text-xs">{l.email}</span></TableCell>
                <TableCell><Badge variant="outline">{ROLE_LABEL[l.role] || l.role || "—"}</Badge></TableCell>
                <TableCell><Badge className={(METHOD[l.method] || {}).cls || "bg-slate-500 hover:bg-slate-500"}>{(METHOD[l.method] || {}).label || l.method}</Badge></TableCell>
                <TableCell className="text-sm"><span className="flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5 text-muted-foreground" title={l.user_agent} />{browserLabel(l.user_agent)}</span></TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{l.ip || "—"}</TableCell>
              </TableRow>
            ))}
            {logs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">{loading ? "Caricamento…" : "Nessun accesso nel periodo selezionato."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
